import { formatDate } from "@/lib/dateUtils";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ServiceTransparencyModal from "@/components/ServiceTransparencyModal";
import EmptyState from "@/components/EmptyState";
import "./CareerPage.css";
import { CuteLoader } from "@/components/CuteLoader";

export default function CareerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  const loadData = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const [dashboardRes, servicesRes] = await Promise.all([
        api.get("/employee/dashboard", { signal: controller.signal }),
        api.get("/services/catalog", { signal: controller.signal })
      ]);
      
      setData(dashboardRes.data);
      setServices(servicesRes.data);
      setError(null);
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError('Could not load career data');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <CuteLoader />;
  }

  if (error) {
    return (
      <div className="career-page-error">
        <div className="error-message">{error}</div>
        <button onClick={() => { setLoading(true); setError(null); loadData(); }} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const employee = data?.employee || {};
  const milestones = data?.milestones || [];
  const isDeparted = employee.employment_status === "departed";

  // Safe tenure: friendly "X years, Y months"; suppresses on any invalid input (never NaN).
  const tenure = computeTenure(employee.hire_date, isDeparted ? employee.termination_date : null);

  return (
    <div className="career-page">
      <div className="career-header">
        <h1>Career</h1>
        <p className="career-subtitle">{isDeparted ? "Your time at GT" : "Your journey at GT"}</p>
      </div>

      <div className="career-body">
        {tenure && (
          <div className="tenure-card">
            <div className="tenure-icon">★</div>
            <div className="tenure-content">
              <div className="tenure-years">{tenure}</div>
              <div className="tenure-label">with GT</div>
            </div>
          </div>
        )}

        {milestones && milestones.length > 0 && (
          <div className="milestones-card">
            <div className="card-header">Milestones</div>
            {milestones.map((m, i) => (
              <div key={i} className="milestone-row">
                <div className="milestone-date">
                  {m.undated ? m.year : formatDate(m.date, { month: "long", year: "numeric" })}
                </div>
                <div className="milestone-description">{m.description}</div>
              </div>
            ))}
          </div>
        )}

        {(!milestones || milestones.length === 0) && (
          <div className="milestones-card">
            <div className="card-header">Milestones</div>
            <EmptyState
              testId="career-milestones-empty"
              line="Your milestones will live here"
              sub={isDeparted ? "A record of the moments that marked your time at GT." : "The first one's coming — every step forward gets marked here."}
              compact
            />
          </div>
        )}

        {/* Live-earning tools (compute at a current rate) are hidden for departed staff —
            their view is a historical record only. */}
        {!isDeparted && services.length > 0 && (
          <div className="services-card">
            <div className="card-header">What you'd earn on every service</div>
            <p className="services-intro">Tap any service to see your take-home at your current rate</p>
            
            {services.map((service) => (
              <div
                key={service.name}
                className="service-row"
                onClick={() => setSelectedService(service.name)}
              >
                <div className="service-name">{service.name}</div>
                <div className="service-price">${service.price}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ServiceTransparencyModal
        isOpen={!!selectedService}
        onClose={() => setSelectedService(null)}
        serviceName={selectedService}
      />
    </div>
  );
}

// Never returns NaN/Infinity — suppresses (null) on any invalid/absent input.
function computeTenure(hireStr, endStr) {
  if (!hireStr || hireStr === "NEEDS INPUT") return null;
  const hire = new Date(hireStr);
  const end = endStr && endStr !== "NEEDS INPUT" ? new Date(endStr) : new Date();
  if (isNaN(hire.getTime()) || isNaN(end.getTime()) || end < hire) return null;
  let months = (end.getFullYear() - hire.getFullYear()) * 12 + (end.getMonth() - hire.getMonth());
  if (end.getDate() < hire.getDate()) months -= 1;
  if (months < 0 || !isFinite(months)) return null;
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} month${m !== 1 ? "s" : ""}`;
  if (m === 0) return `${y} year${y !== 1 ? "s" : ""}`;
  return `${y} year${y !== 1 ? "s" : ""}, ${m} month${m !== 1 ? "s" : ""}`;
}
